import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { formatClock } from '@/features/trips/format';
import type { DataDelayedAlert } from './types';

/**
 * A DATA-DELAYED event in the alerts feed (slice 76) — the TENTH item shape. A
 * tracker's real GPS frames are arriving more than 15 min behind their fix time
 * (the M6D826 SIM-credit case: buffered points uploaded up to 168 min late). In
 * the SAME "tracker link unhealthy" family as `tracker_offline` / `gps_fix_lost`,
 * so it is modelled on `TrackerHealthRow`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * 🔴 THIS ROW GOES TO THE VEHICLE, NEVER TO A MAP.
 * ═════════════════════════════════════════════════════════════════════════════
 * A stale point stream has no trustworthy location — the wire item carries no
 * `observedLat`/`observedLon`, and it must never be given one (position 27 all
 * over again). So this is a `Link` straight to `/vehicles/:id`, not a
 * select-and-centre-a-map button, and it deliberately does NOT call `onSelect`
 * (see `TrackerHealthRow` for the full reasoning). `hasObservedPosition` returns
 * false for it, so no map or deep-link call site can reach for coordinates it
 * does not have.
 *
 * COPY (EN + Mahad's verbatim Somali, `alerts.dataDelayed.*`):
 *   - title:  "Data delayed" / "Xogta oo daahday"
 *   - detail: "Points were arriving about {{minutes}} min behind." (from the
 *     frozen `delayMinutes`)
 *   - resolved: "Resolved {{time}}" once `endedAt` is set (the data caught up).
 *
 * The tag chip reuses `trackerHealth.tag.tracker` — this is the tracker-link
 * family, and reusing it avoids inventing a second Somali label for the same
 * concept.
 *
 * Read/unread is a leading dot + a weight change, never colour alone — the same
 * language as every other feed row. Tokens only; no hex literal enters this file.
 */
export function DataDelayedRow({
  alert,
  plate,
  locale,
  timeZone,
  t,
}: {
  alert: DataDelayedAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  t: TFunction;
}) {
  const resolved = alert.endedAt != null;
  // An OPEN episode (data still arriving late) draws the eye; a resolved one is
  // quiet, matching how TrackerHealthRow treats a recovered episode.
  const isAttention = !resolved;

  const endedTime = alert.endedAt
    ? formatClock(locale, Date.parse(alert.endedAt), timeZone)
    : null;

  // Open → the "how far behind" detail line; resolved → the recovery time.
  const metaText = resolved
    ? t('alerts.dataDelayed.resolved', { time: endedTime })
    : t('alerts.dataDelayed.detail', { minutes: alert.delayMinutes });

  const tagText = t('trackerHealth.tag.tracker');
  const tagClass = isAttention ? 'bg-raad-warn/18 text-raad-warn' : 'bg-raad-slate/12 text-raad-slate';

  const titleClass =
    isAttention && !alert.read ? 'font-semibold text-raad-text' : 'font-medium text-raad-muted';
  const dotClass = alert.read ? 'bg-transparent' : isAttention ? 'bg-raad-accent' : 'bg-raad-slate';

  return (
    <li>
      <Link
        to={`/vehicles/${alert.vehicleId}`}
        data-testid="data-delayed-row"
        data-type={alert.type}
        data-severity={alert.severity}
        data-alert-id={alert.id}
        className={[
          'block w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
          isAttention
            ? 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15'
            : 'border-raad-line-5 bg-raad-tile/60 hover:border-raad-line-12',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={['truncate text-sm', titleClass].join(' ')}>
                {t('alerts.dataDelayed.title')}
              </span>
              <span
                data-testid="data-delayed-tag"
                className={[
                  'flex-none rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  tagClass,
                ].join(' ')}
              >
                {tagText}
              </span>
              {resolved ? (
                <span
                  data-testid="data-delayed-resolved"
                  className="flex-none rounded-md bg-raad-slate/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-slate"
                >
                  {t('trackerHealth.resolved')}
                </span>
              ) : null}
              {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
            </div>

            {plate ? (
              <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
            ) : null}

            <div className="mt-1 text-[12px] text-raad-muted">{metaText}</div>
          </div>
        </div>
      </Link>
    </li>
  );
}
